import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { DEFAULT_MENU_HEIGHT } from "components/basic/header";
import ChargerCard, { ChargerCardHandle } from "./chargerCard";
import Button from "components/basic/button";
import { useNavigate } from "react-router-dom";
import {
  EvChargerServiceSortBy,
  useGetEvChargerServiceRecommendationsQuery,
} from "generated/graphql";
import { ChargerEstimatorToolsPagesProps } from "pages/chargerEstimator";
import ScrollContainer from "components/basic/scrollContainer";
import NoCoverageMessage from "./components/NoCoverageMessage";
import SortFilter from "./components/SortFilter";
import QuoteCarousel from "./components/QuoteCarousel";

export const DEFAULT_BOTTOM_NAV_HEIGHT = 80;

const SelectEstimate: React.FC<ChargerEstimatorToolsPagesProps> = ({
  chargerEstimatorData,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("estimator");
  const navigate = useNavigate();

  const {
    vehicleType,
    houseType,
    chargerSetup,
    chargerLocation,
    wireLength,
    latitude,
    longitude,
    address,
  } = chargerEstimatorData;

  // How the user wants the installers that serve them ordered. It only reorders
  // the list — the server still returns whoever's service radius covers the
  // address, whichever option is picked.
  const [sortBy, setSortBy] = useState<EvChargerServiceSortBy>(
    EvChargerServiceSortBy.Distance,
  );

  const [{ data: fetchedData, fetching }] =
    useGetEvChargerServiceRecommendationsQuery({
      requestPolicy: "network-only",
      variables: {
        vehicleType,
        houseType,
        evseType: `${chargerSetup} (${chargerLocation})`,
        wireLength,
        limit: 3,
        sortBy,
        userLat: latitude,
        userLng: longitude,
      },
    });

  const services = fetchedData?.getEVChargerServiceRecommendations ?? [];

  // The backend only returns installers whose service radius covers this
  // address, so an empty result means nobody serves the user.
  const hasNoCoverage = !fetching && services.length === 0;

  // One card at a time below md; the full comparison row above it.
  const isCarousel = useMediaQuery(theme.breakpoints.down("md"));
  const [activeIndex, setActiveIndex] = useState(0);

  // Next mirrors the visible card's "Expand", carrying that card's selections.
  // On the desktop row nothing moves the index, so it stays the first card.
  const cardRefs = useRef<(ChargerCardHandle | null)[]>([]);

  // Sorting reorders the list underneath the user, so start from the top again.
  useEffect(() => {
    setActiveIndex(0);
  }, [sortBy]);

  // The header gains a row at narrow widths (51px wide, 61px below md), so
  // DEFAULT_MENU_HEIGHT under-measures it there and the pinned footer lands
  // below the fold. Measure what actually sits above us instead.
  const rootRef = useRef<HTMLDivElement>(null);
  const [offsetTop, setOffsetTop] = useState(DEFAULT_MENU_HEIGHT + 1);

  useLayoutEffect(() => {
    const update = () => {
      const element = rootRef.current;
      if (!element) {
        return;
      }
      const top = Math.round(
        element.getBoundingClientRect().top + window.scrollY,
      );
      setOffsetTop((previous) => (previous === top ? previous : top));
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const handleNext = () => {
    const card = cardRefs.current[activeIndex] ?? cardRefs.current[0];
    if (card) {
      card.expand();
      return;
    }
    navigate("?currentPage=quote");
  };

  // Wide screens get the comparison row; it scrolls sideways rather than
  // wrapping, which used to drop the third quote onto a second row below the
  // fold where nothing hinted it existed.
  const isRow = !isCarousel && !hasNoCoverage;

  const mainSx = {
    flex: 1,
    minHeight: 0,
    paddingTop: 0,
    ...(isRow
      ? {
          overflowX: "auto",
          overflowY: "hidden",
          paddingX: 2,
          paddingBottom: 2,
        }
      : {
          display: "flex",
          justifyContent: "center",
          alignItems: "stretch",
          gap: theme.spacing(2),
          // The carousel runs edge to edge and owns its own footer padding.
          paddingX: isCarousel ? 0 : 2,
          paddingBottom: isCarousel ? 0 : 2,
        }),
  };

  // `max-content` + auto margins centres the row while it fits and falls back to
  // a normal left-aligned scroll once it does not — plain `justify-content:
  // center` would make the first card unreachable when it overflows.
  const rowSx = {
    display: "flex",
    alignItems: "stretch",
    gap: theme.spacing(2),
    height: "100%",
    width: "max-content",
    margin: "0 auto",
    "& > *": { flex: "0 0 360px", minWidth: 0 },
  };

  return (
    <Box
      ref={rootRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: `calc(100dvh - ${offsetTop}px)`,
        backgroundColor: theme.palette.warning.main,
      }}
    >
      {/* Sort control, hidden when there is nothing to sort */}
      {!hasNoCoverage && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: theme.spacing(2),
            paddingBottom: theme.spacing(1),
          }}
        >
          <SortFilter value={sortBy} onChange={setSortBy} />
        </div>
      )}

      {/* Main content */}
      <Box sx={mainSx}>
        {hasNoCoverage ? (
          <NoCoverageMessage
            address={address}
            onChangeAddress={() => navigate("?currentPage=addressPage")}
          />
        ) : isCarousel ? (
          <QuoteCarousel
            services={services}
            chargerConfigData={chargerEstimatorData}
            activeIndex={activeIndex}
            onActiveIndexChange={setActiveIndex}
            onExpand={handleNext}
            cardRefs={cardRefs}
          />
        ) : (
          <Box sx={rowSx}>
            {services.map((service, index) => (
              <ScrollContainer key={service.id}>
                <ChargerCard
                  ref={(handle) => {
                    cardRefs.current[index] = handle;
                  }}
                  service={service}
                  chargerConfigData={chargerEstimatorData}
                  allServices={services}
                  serviceIndex={index}
                />
              </ScrollContainer>
            ))}
          </Box>
        )}
      </Box>
      {/* The carousel carries its own Expand footer, so the flow nav would just
          crowd the screen. It stays when there is no carousel to replace it. */}
      <Box
        sx={{
          display: isCarousel && !hasNoCoverage ? "none" : "flex",
          backgroundColor: theme.palette.primary.main,
          minHeight: `${DEFAULT_BOTTOM_NAV_HEIGHT}px`,
          flexShrink: 0,
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          paddingX: { xs: 1, sm: 0 },
          paddingY: { xs: 1, sm: 0 },
          gap: theme.spacing(2),
        }}
      >
        <Button
          style={{
            color: theme.palette.common.white,
            fontSize: "1.4em",
            fontWeight: "bold",
            padding: `0 ${theme.spacing(3.5)}`,
          }}
          variant="contained"
          color="warning"
          onClick={() => navigate("?currentPage=reviewDetails")}
          size="large"
        >
          {t("nav.back")}
        </Button>

        <Button
          style={{
            color: theme.palette.common.black,
            fontSize: "1.4em",
            fontWeight: "bold",
            padding: `0 ${theme.spacing(3.5)}`,
          }}
          variant="contained"
          color="secondary"
          onClick={handleNext}
          size="large"
          disabled={services.length === 0}
        >
          {t("nav.next")}
        </Button>
      </Box>
    </Box>
  );
};

export default SelectEstimate;
